'use client';

import { ChevronDown, ExternalLink, X } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';

import { useNWAElementImpression } from '@ria-client/segment/hooks/useNWAElementImpression';
import { useNWAElementInteracted } from '@ria-client/segment/hooks/useNWAElementInteracted';
import { ADVISORS_MATCH_BASE_URL } from '@ria-client/utils/advisorMatch/urls';
import { useAdUrl } from '../lib/use-ad-url';
import type { NodeProps } from '../types';

export interface NerdwalletAdvisorsBannerProps extends Record<string, unknown> {
  buttonLargerText?: string;
  buttonSmallerText?: string;
  collapsed?: boolean;
  allBordersRounded?: boolean;
  headingTextExpanded?: string;
  headingTextCollapsed?: string;
  buttonCtaUrl?: string;
  variant?: 'large' | 'medium';
  largeLogo?: BannerImage;
  largeImage?: BannerImage;
  smallLogo?: BannerImage;
}

interface BannerImage {
  url: string;
  alt: string;
}

const DEFAULT_PROPS = {
  headingTextExpanded:
    'Get a custom financial plan and unlimited access to a Certified Financial Planner',
  headingTextCollapsed: 'Get a custom financial plan',
  buttonLargerText: "Match With an Advisor - it's Free",
  buttonSmallerText: 'Get Matched',
  buttonCtaUrl: `${ADVISORS_MATCH_BASE_URL}/currently-has-advisor`,
  variant: 'medium' as const,
  largeImage: {
    url: 'https://cdns3.nerdwallet.com/nerdwallet-advisors/nwa-banner-main-img.png',
    alt: 'Illustration',
  },
  largeLogo: {
    url: 'https://cdns3.nerdwallet.com/bain-components/nw-advisors/nw-advisors-logo-large.svg',
    alt: 'NerdWallet Advisors',
  },
  smallLogo: {
    url: 'https://cdns3.nerdwallet.com/nerdwallet-advisors/nwa-match-small-logo.png',
    alt: 'NerdWallet Advisors',
  },
};

const AD_PARAMS = { utm_content: 'nwadstickybanner' };

export function NerdwalletAdvisorsBanner({ props }: NodeProps<NerdwalletAdvisorsBannerProps>) {
  const {
    allBordersRounded = false,
    buttonCtaUrl = DEFAULT_PROPS.buttonCtaUrl,
    buttonLargerText = DEFAULT_PROPS.buttonLargerText,
    buttonSmallerText = DEFAULT_PROPS.buttonSmallerText,
    collapsed = false,
    headingTextCollapsed = DEFAULT_PROPS.headingTextCollapsed,
    headingTextExpanded = DEFAULT_PROPS.headingTextExpanded,
    largeImage = DEFAULT_PROPS.largeImage,
    largeLogo = DEFAULT_PROPS.largeLogo,
    smallLogo = DEFAULT_PROPS.smallLogo,
    variant = DEFAULT_PROPS.variant,
  } = props;
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const [isOpen, setIsOpen] = useState(true);
  const href = useAdUrl(buttonCtaUrl, AD_PARAMS);
  const { ensureViewed, impressionId, ref } = useNWAElementImpression({
    component: 'nwa_stickybanner',
    destinationUrl: href,
  });
  const { trackElementInteracted } = useNWAElementInteracted();

  if (!isOpen) return null;

  function trackClick() {
    ensureViewed();
    void trackElementInteracted({ component: 'nwa_stickybanner', impressionId });
  }

  return (
    <aside
      aria-label="NerdWallet Advisors"
      className={`sticky bottom-0 z-20 overflow-hidden bg-[var(--content-blue-extra-light)] p-4 ${allBordersRounded ? 'rounded' : 'rounded-t'}`}
      ref={ref}
    >
      {isCollapsed ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-col items-start gap-3 min-[721px]:flex-row min-[721px]:items-center min-[721px]:gap-7">
            <p className="typography-body-small font-bold">{headingTextCollapsed}</p>
            <BannerLink href={href} onClick={trackClick}>
              {buttonSmallerText}
            </BannerLink>
          </div>
          <div className="grid shrink-0 grid-flow-col auto-cols-max items-center gap-3">
            <Image
              alt={smallLogo.alt}
              className="hidden h-auto max-h-8 w-auto max-w-28 min-[721px]:block"
              height={32}
              src={smallLogo.url}
              width={112}
            />
            <AdLabel />
            <IconButton label="Dismiss banner" onClick={() => setIsOpen(false)}>
              <X aria-hidden="true" size={20} />
            </IconButton>
          </div>
        </div>
      ) : (
        <div className={variant === 'large' ? 'flex justify-between gap-4' : ''}>
          <div className={variant === 'large' ? 'w-full min-[721px]:w-[70%]' : 'w-full'}>
            <Image
              alt={largeLogo.alt}
              className="h-auto max-h-[31px] w-auto max-w-[200px] min-[481px]:max-w-[300px] min-[721px]:max-w-[536px]"
              height={31}
              src={largeLogo.url}
              width={536}
            />
            <p
              className={`font-sans font-extrabold text-[var(--content-blue-dark)] ${variant === 'large' ? 'my-5 text-xl leading-6 min-[721px]:mt-20 min-[721px]:mb-10 min-[721px]:text-3xl min-[721px]:leading-9' : 'my-5 text-base leading-5 min-[721px]:mt-10 min-[721px]:text-xl min-[721px]:leading-6'}`}
            >
              {headingTextExpanded}
            </p>
            <BannerLink href={href} onClick={trackClick}>
              <span className="hidden min-[481px]:inline">{buttonLargerText}</span>
              <span className="min-[481px]:hidden">{buttonSmallerText}</span>
            </BannerLink>
          </div>
          {variant === 'large' ? (
            <Image
              alt={largeImage.alt}
              className="hidden h-auto max-h-[190px] w-auto max-w-[257px] self-end min-[721px]:block"
              height={190}
              src={largeImage.url}
              width={257}
            />
          ) : null}
          <div className="absolute top-4 right-4 flex items-center gap-2.5">
            <AdLabel />
            <IconButton
              className="hidden min-[721px]:inline-flex"
              label="Collapse banner"
              onClick={() => setIsCollapsed(true)}
            >
              <ChevronDown aria-hidden="true" size={20} />
            </IconButton>
            <IconButton
              className="min-[721px]:hidden"
              label="Dismiss banner"
              onClick={() => setIsOpen(false)}
            >
              <X aria-hidden="true" size={20} />
            </IconButton>
          </div>
        </div>
      )}
    </aside>
  );
}

interface BannerLinkProps {
  children: React.ReactNode;
  href: string;
  onClick: () => void;
}

function BannerLink({ children, href, onClick }: BannerLinkProps) {
  return (
    <a
      className="inline-flex min-h-10 items-center gap-3 rounded-lg bg-[var(--content-blue)] px-4 py-2 font-sans font-medium text-white no-underline transition-colors hover:bg-[var(--content-blue-dark)] focus-visible:focus-ring"
      href={href}
      onClick={onClick}
    >
      {children}
      <ExternalLink aria-hidden="true" size={16} />
    </a>
  );
}

function AdLabel() {
  return (
    <span className="rounded-lg bg-neutral-500 px-2 py-1 text-[10px] leading-3 text-white">
      <span className="min-[721px]:hidden">AD</span>
      <span className="hidden min-[721px]:inline">Advertisement</span>
    </span>
  );
}

interface IconButtonProps {
  children: React.ReactNode;
  className?: string;
  label: string;
  onClick: () => void;
}

function IconButton({ children, className = '', label, onClick }: IconButtonProps) {
  return (
    <button
      aria-label={label}
      className={`rounded p-1 text-foreground hover:bg-black/5 focus-visible:focus-ring ${className}`}
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
}
